import { useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { AMAZON_SELLER_URL, landingCopy, TEAM_APP_URL, type LandingLocale } from "./landingCopy"
import { useLandingLocale } from "./landingLocale"
import "./PublicLanding.css"

export const useLandingDocument = (locale: LandingLocale, title: string, description: string) => {
  useEffect(() => {
    document.documentElement.classList.add("landing-site")
    document.body.classList.add("landing-site")
    return () => {
      document.documentElement.classList.remove("landing-site")
      document.body.classList.remove("landing-site")
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale
    document.title = title

    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement("meta")
      meta.setAttribute("name", "description")
      document.head.appendChild(meta)
    }
    meta.setAttribute("content", description)

    let robots = document.querySelector('meta[name="robots"]')
    if (!robots) {
      robots = document.createElement("meta")
      robots.setAttribute("name", "robots")
      document.head.appendChild(robots)
    }
    robots.setAttribute("content", "index, follow")
  }, [ description, locale, title ])
}

export const LangToggle = ({
  locale,
  onChange,
}: {
  locale: LandingLocale
  onChange: (next: LandingLocale) => void
}) => (
  <div className="landing-lang" role="group" aria-label="Language">
    <button
      type="button"
      className={locale === "en" ? "is-active" : undefined}
      onClick={() => onChange("en")}
    >
      EN
    </button>
    <span aria-hidden="true">|</span>
    <button
      type="button"
      className={locale === "de" ? "is-active" : undefined}
      onClick={() => onChange("de")}
    >
      DE
    </button>
  </div>
)

export const LandingHeader = ({
  locale,
  onLocaleChange,
  contactHref = "/impressum#contact",
}: {
  locale: LandingLocale
  onLocaleChange: (next: LandingLocale) => void
  contactHref?: string
}) => {
  const copy = landingCopy(locale)
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const header = headerRef.current
    if (!header) return

    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--landing-header-height",
        `${header.offsetHeight}px`,
      )
    }

    syncHeaderHeight()
    const observer = new ResizeObserver(syncHeaderHeight)
    observer.observe(header)
    window.addEventListener("resize", syncHeaderHeight)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", syncHeaderHeight)
    }
  }, [ locale ])

  return (
    <header className="landing-header" ref={headerRef}>
      <div className="landing-nav">
        <Link to="/" className="landing-logo">
          <img src={`${process.env.PUBLIC_URL}/logo_viridian.png`} alt="" aria-hidden />
          <span>Viridian GmbH</span>
        </Link>
        <nav className="landing-nav-links" aria-label="Primary">
          <a href="/#about">{copy.navAbout}</a>
          <a href="/#brands">{copy.navBrands}</a>
          <a href="/#partners">{copy.navPartners}</a>
          <a href="/#careers">{copy.navCareers}</a>
          <a href={contactHref}>{copy.navContact}</a>
          <a className="landing-nav-login" href={TEAM_APP_URL}>
            {copy.teamLogin}
          </a>
          <LangToggle locale={locale} onChange={onLocaleChange} />
        </nav>
      </div>
    </header>
  )
}

export const LandingFooter = ({ locale }: { locale: LandingLocale }) => {
  const copy = landingCopy(locale)
  const year = new Date().getFullYear()

  return (
    <footer className="landing-footer">
      <div className="landing-footer-inner">
        <div className="landing-footer-brand">
          <p className="landing-footer-name">Viridian GmbH</p>
          <p className="landing-footer-tagline">{copy.footerTagline}</p>
        </div>
        <nav className="landing-footer-links" aria-label="Footer">
          <Link to="/impressum">{copy.impressumTitle}</Link>
          <a href={AMAZON_SELLER_URL} target="_blank" rel="noopener noreferrer">
            {copy.contactSellerProfile}
          </a>
        </nav>
        <p className="landing-footer-copy">
          © {year} Viridian GmbH. {copy.copyright}
        </p>
      </div>
    </footer>
  )
}
